import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { WorkflowComponentProps } from '../../Workflow/Workflow.meta';
import { hasAccessToEditProfileSelector } from '../../CommonSelectors/CommonAreaSelector';
import { useResetModuleWrapperStyles } from '../../_hooks/useResetModuleWrapperStyles';
import { useIncome } from './useIncome';
import { ScrollHereOnce } from '../../CommonComponents/ScrollHereOnce';
import { Wrapper } from '../components/Wrapper/Wrapper';
import { ProfilePageHeader } from '../components/ProfilePageHeader/ProfilePageHeader';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { WorkflowFooter } from '../../Workflow/components/WorkflowFooter/WorkflowFooter';
import { ErrorState } from '../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { IncomeType } from './api/useIncomeQuery';
import { Employment } from './tables/Employment/Employment';
import { Business } from './tables/Business/Business';
import { RealEstate } from './tables/RealEstate/RealEstate';
import { SocialSecurity } from './tables/SocialSecurity/SocialSecurity';
import { OtherIncome } from './tables/OtherIncome/OtherIncome';
import {
  IncomeToDelete,
  IncomeToUpsert,
  CLONE_INCOME_SUCCESS_MESSAGE,
  getClonedIncomeName,
  isClonableIncomeType,
} from './Income.meta';
import { DeleteIncomeModal } from './components/DeleteIncomeModal/DeleteIncomeModal';
import { UpsertIncomeModal } from './components/UpsertIncomeModal/UpsertIncomeModal';
import { ProfileTablesLoading } from '../components/ProfileTablesLoading/ProfileTablesLoading';
import { useUpsertIncomeMutation } from './api/useUpsertIncomeMutation';
import { IncomeModel } from './api/useIncomeQuery';
import { buildCloneIncomeBody } from './utils/cloneIncome';
import { toast } from '../../CommonComponents/OPL/Toast/Toast';

const TablesDict = {
  [IncomeType.Salary]: Employment,
  [IncomeType.Business]: Business,
  [IncomeType.RealEstate]: RealEstate,
  [IncomeType.SocialSecurity]: SocialSecurity,
  [IncomeType.Other]: OtherIncome,
};

interface Props {
  clientId: string;
  workflowProps?: WorkflowComponentProps;
}

export const Income = ({ clientId, workflowProps }: Props) => {
  useResetModuleWrapperStyles({ isActive: !workflowProps });

  const canEdit = useSelector(hasAccessToEditProfileSelector);
  const { isSuccess } = useIncome({ clientId });

  const [incomeToUpsert, setIncomeToUpsert] = useState<IncomeToUpsert | null>(null);

  return (
    <>
      <ScrollHereOnce options={false} />

      <Wrapper isWorkflow={!!workflowProps}>
        <ProfilePageHeader
          title="Income"
          description="Add any salaries, business income and real estate income, social security and other income."
        >
          {isSuccess && canEdit && (
            <Button leftIcon="fas fa-plus" onClick={() => setIncomeToUpsert({})}>
              Add Income
            </Button>
          )}
        </ProfilePageHeader>

        <IncomeContent
          clientId={clientId}
          workflowProps={workflowProps}
          incomeToUpsert={incomeToUpsert}
          setIncomeToUpsert={setIncomeToUpsert}
        />
      </Wrapper>

      {workflowProps && (
        <WorkflowFooter
          isFirstStep={workflowProps.isFirstStep}
          onPrevious={workflowProps.onPrevious}
          onNext={workflowProps.onNext}
        />
      )}
    </>
  );
};

interface ContentProps {
  clientId: string;
  workflowProps?: WorkflowComponentProps;
  incomeToUpsert: IncomeToUpsert | null;
  setIncomeToUpsert: (incomeToUpsert: IncomeToUpsert | null) => void;
}

const IncomeContent = ({
  clientId,
  workflowProps,
  incomeToUpsert,
  setIncomeToUpsert,
}: ContentProps) => {
  const canEdit = useSelector(hasAccessToEditProfileSelector);
  const [incomeToDelete, setIncomeToDelete] = useState<IncomeToDelete | null>(null);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const { isLoading, isError, refetch, data } = useIncome({ clientId });
  const { mutateAsync: upsertIncome } = useUpsertIncomeMutation({ clientId });

  useEffect(() => {
    setIsFormDirty(false);
  }, [incomeToUpsert]);

  const handleFormDirtyChange = useCallback((dirty: boolean) => {
    setIsFormDirty(prev => (prev === dirty ? prev : dirty));
  }, []);

  if (isLoading) {
    return <ProfileTablesLoading isWorkflow={!!workflowProps} />;
  }

  if (isError) {
    return <ErrorState onAction={refetch} />;
  }

  if (!data) {
    return null;
  }

  const handleClone = async (income: IncomeModel) => {
    if (!isClonableIncomeType(income.IncomeType)) {
      return;
    }
    const body = buildCloneIncomeBody({ clientId, income, data });

    if (!body) {
      return;
    }

    try {
      await upsertIncome(body);
      toast.success(CLONE_INCOME_SUCCESS_MESSAGE);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  return (
    <>
      <Box display="flex" flexDirection="column" css="gap: 32px">
        {data.Options.IncomeTypeOptions.map(incomeType => {
          const IncomeTable = TablesDict[incomeType.value];
          const isClonable = isClonableIncomeType(incomeType.value);

          return (
            <IncomeTable
              key={incomeType.value}
              clientId={clientId}
              incomeType={incomeType.value}
              data={data}
              setIncomeToUpsert={setIncomeToUpsert}
              setIncomeToDelete={setIncomeToDelete}
              {...(isClonable && canEdit && { onClone: handleClone })}
              workflowProps={workflowProps}
            />
          );
        })}
      </Box>

      {incomeToUpsert && (
        <UpsertIncomeModal
          clientId={clientId}
          income={incomeToUpsert}
          data={data}
          onClose={() => setIncomeToUpsert(null)}
          onSuccess={() => setIncomeToUpsert(null)}
          onIncomeTypeChange={incomeType => setIncomeToUpsert({ IncomeType: incomeType })}
          incomeToDelete={incomeToDelete}
          setIncomeToDelete={setIncomeToDelete}
          workflowProps={workflowProps}
          onClone={() => {
            if (!isClonableIncomeType(incomeToUpsert.IncomeType)) {
              return;
            }

            const incomeTitle =
              data.Options.IncomeTypeOptions.find(
                option => option.value === incomeToUpsert.IncomeType,
              )?.label || 'Income';

            setIncomeToUpsert({
              ...incomeToUpsert,
              isClone: true,
              Name: getClonedIncomeName({ incomeName: incomeToUpsert.Name, incomeTitle }),
            });
            setIsFormDirty(false);
          }}
          isCloneDisabled={isFormDirty}
          onDirtyChange={handleFormDirtyChange}
        />
      )}

      {incomeToDelete && (
        <DeleteIncomeModal
          clientId={clientId}
          income={incomeToDelete}
          onClose={() => setIncomeToDelete(null)}
          onDelete={() => {
            setIncomeToDelete(null);
            setIncomeToUpsert(null);
          }}
        />
      )}
    </>
  );
};
